import { useState, type FormEvent } from 'react';
import { inputCls, labelCls } from '@/components/base/Modal';
import {
  recordPurchasePayment,
  deletePurchasePayment,
  type PurchasePaymentDetail,
} from '@/services/purchases';

interface PurchasePaymentsSectionProps {
  purchaseId: string;
  payments: PurchasePaymentDetail[];
  balance: number;
  onChanged: () => void;
}

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function PurchasePaymentsSection({
  purchaseId,
  payments,
  balance,
  onChanged,
}: PurchasePaymentsSectionProps) {
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [removingId, setRemovingId] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const value = Number(amount);
    if (!value || value <= 0) {
      setError('Ingresa un monto válido para el abono.');
      return;
    }
    setSaving(true);
    const { error: saveError } = await recordPurchasePayment({
      purchaseId,
      amount: value,
      paidAt: paidAt ? new Date(`${paidAt}T12:00:00`).toISOString() : '',
      notes,
    });
    setSaving(false);
    if (saveError) {
      setError(saveError);
      return;
    }
    setAmount('');
    setNotes('');
    setPaidAt(new Date().toISOString().slice(0, 10));
    onChanged();
  }

  async function handleDelete(id: string) {
    setRemovingId(id);
    const { error: delError } = await deletePurchasePayment(id, purchaseId);
    setRemovingId(null);
    if (delError) {
      setError(delError);
      return;
    }
    onChanged();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-foreground-700">Abonos registrados</p>
        <span className={`text-xs font-medium ${balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
          Saldo: ${balance.toLocaleString()}
        </span>
      </div>

      {payments.length === 0 ? (
        <p className="text-sm text-foreground-400">Aún no hay abonos para esta compra.</p>
      ) : (
        <div className="space-y-2">
          {payments.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 border border-background-200 rounded-lg px-3 py-2.5"
            >
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-hand-coin-line text-emerald-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-900">${p.amount.toLocaleString()}</p>
                <p className="text-xs text-foreground-500 truncate">
                  {formatDate(p.paidAt)}
                  {p.notes ? ` · ${p.notes}` : ''}
                </p>
              </div>
              <button
                onClick={() => handleDelete(p.id)}
                disabled={removingId === p.id}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
                title="Eliminar abono"
              >
                <i className={removingId === p.id ? 'ri-loader-4-line animate-spin' : 'ri-delete-bin-line'}></i>
              </button>
            </div>
          ))}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="border border-dashed border-background-300 rounded-lg p-3 space-y-3 bg-background-50"
      >
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Monto del abono ($)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className={`${inputCls} w-full`}
            />
          </div>
          <div>
            <label className={labelCls}>Fecha</label>
            <input
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              className={`${inputCls} w-full`}
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Notas (opcional)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Efectivo, transferencia, etc."
            className={`${inputCls} w-full`}
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="w-full text-sm px-4 py-2.5 rounded-lg bg-accent-500 text-white font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-1.5"></i>Registrando...
            </>
          ) : (
            <>
              <i className="ri-add-line mr-1.5"></i>Registrar abono
            </>
          )}
        </button>
      </form>
    </div>
  );
}